import {useEffect, useMemo, useRef, useState} from 'react';
import type {KeyboardEvent} from 'react';
import type {NovaCategoryOption} from '../modals/CreateAgentModal';
import {isCategoryNameTakenError, isReservedCategoryName, normaliseCategoryName} from '../shared/categoryName';

interface UseInlineCategoryCreationOptions {
	// Categories currently known to the catalogue (refetched). The merge and prune below are relative
	// to this list.
	categoryOptions: NovaCategoryOption[];
	// Persist a new category; resolves with the created option.
	onCreateCategory: (name: string) => Promise<NovaCategoryOption>;
	// Called after a successful create so the caller can select the new category.
	onCategoryCreated: (category: NovaCategoryOption) => void;
}

export interface InlineCategoryCreation {
	// categoryOptions plus any just-created categories not yet reflected in them, so a new chip shows
	// immediately without waiting for the catalogue refetch.
	allCategories: NovaCategoryOption[];
	isAdding: boolean;
	name: string;
	isCreating: boolean;
	error: boolean;
	// The typed name matches an existing category, ignoring case and surrounding spaces. The service
	// refuses those, so the caller blocks Add and says why instead.
	isDuplicate: boolean;
	startAdding: () => void;
	setName: (name: string) => void;
	confirm: () => Promise<void>;
	handleKeyDown: (event: KeyboardEvent<HTMLDivElement>) => void;
}

// Shared inline "create a category" flow used by both the New-agent modal and the Prompt tab's
// category picker: a name field that persists via `onCreateCategory`, selects the result, and keeps
// the new category visible as a chip until the catalogue refetch catches up. Extracted so the two
// call sites can't drift.
export function useInlineCategoryCreation({
	categoryOptions,
	onCreateCategory,
	onCategoryCreated,
}: UseInlineCategoryCreationOptions): InlineCategoryCreation {
	const [createdCategories, setCreatedCategories] = useState<NovaCategoryOption[]>([]);
	const [isAdding, setIsAdding] = useState(false);
	const [name, setName] = useState('');
	const [isCreating, setIsCreating] = useState(false);
	const [error, setError] = useState(false);

	// The caller may unmount (agent switch, modal close) while a create is in flight; guard the
	// post-await writes to avoid setState-after-unmount.
	const isMountedRef = useRef(true);
	useEffect(
		() => () => {
			isMountedRef.current = false;
		},
		[]
	);

	// Once a just-created category appears in the refetched options it is canonical, so drop the local
	// copy. This also prevents a category later deleted from the catalogue from lingering as a stale
	// chip: it leaves categoryOptions and is no longer held here either.
	useEffect(() => {
		const known = new Set(categoryOptions.map(category => category.categoryId));
		setCreatedCategories(previous => previous.filter(category => !known.has(category.categoryId)));
	}, [categoryOptions]);

	const allCategories = useMemo(() => {
		const known = new Set(categoryOptions.map(category => category.categoryId));
		return [...categoryOptions, ...createdCategories.filter(category => !known.has(category.categoryId))];
	}, [categoryOptions, createdCategories]);

	// Names the service refused as taken although the catalogue did not show them yet (created
	// elsewhere moments ago), normalised. Remembered so the field explains the refusal and stays blocked.
	const [takenNames, setTakenNames] = useState<string[]>([]);
	// Only until the catalogue refetches: by then a name that really is taken is in categoryOptions
	// itself, and one whose category was deleted since is free again.
	useEffect(() => setTakenNames([]), [categoryOptions]);

	const isDuplicate = useMemo(() => {
		const normalised = normaliseCategoryName(name);
		return (
			normalised !== '' &&
			(isReservedCategoryName(normalised) ||
				takenNames.includes(normalised) ||
				allCategories.some(category => normaliseCategoryName(category.name) === normalised))
		);
	}, [name, allCategories, takenNames]);

	const confirm = async (): Promise<void> => {
		const trimmedName = name.trim();
		if (!trimmedName || isCreating || isDuplicate) {
			return;
		}
		setIsCreating(true);
		setError(false);
		try {
			const created = await onCreateCategory(trimmedName);
			if (!isMountedRef.current) {
				return;
			}
			setCreatedCategories(previous => [...previous, created]);
			onCategoryCreated(created);
			setName('');
			setIsAdding(false);
		} catch (createError) {
			if (!isMountedRef.current) {
				return;
			}
			if (isCategoryNameTakenError(createError)) {
				setTakenNames(previous => [...previous, normaliseCategoryName(trimmedName)]);
			} else {
				setError(true);
			}
		} finally {
			if (isMountedRef.current) {
				setIsCreating(false);
			}
		}
	};

	// TextField exposes no onKeyDown, so Enter-to-confirm is wired on a wrapping div — the input's
	// native keydown still bubbles up to it.
	const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
		if (event.key === 'Enter') {
			event.preventDefault();
			void confirm();
		}
	};

	return {
		allCategories,
		isAdding,
		name,
		isCreating,
		error,
		isDuplicate,
		startAdding: () => setIsAdding(true),
		setName,
		confirm,
		handleKeyDown,
	};
}
